import React, { useState } from 'react'
import { Folder, DataSource } from '../types'
import { getButtonClasses, getButtonStyles } from '../constants'
import { TbArrowBackUp } from "react-icons/tb";
import { FaChartBar, FaPlus, FaFolder } from 'react-icons/fa';
import { RiFolderCloudFill, RiDeleteBin2Fill } from "react-icons/ri";
import { BsFolderPlus } from "react-icons/bs";
import { HiOutlineFolderDownload } from "react-icons/hi";
import { MdDriveFileMove, MdOutlineSelectAll } from "react-icons/md";
import { FaUserDoctor } from "react-icons/fa6";
import { VscFeedback } from "react-icons/vsc";
import { useTranslate } from '../hooks/useTranslate';
import ContextualHelpLink from './training/ContextualHelpLink';

interface HeaderSectionProps {
    isInFolderView: boolean
    selectedFolder: Folder | null
    onBackToMain: () => void
    onShowCreateFolder: () => void
    onAddDataObject: () => void
    onShowFeedback?: () => void
    disabled?: boolean
    // Multi-select props
    isSelecting?: boolean
    selectedCount?: number
    onToggleSelect?: () => void
    onBulkMove?: () => void
    onBulkDelete?: () => void
    onSelectAll?: () => void
    onClearSelection?: () => void
    hasSelectableItems?: boolean // New prop to determine if Select button should be shown
    // Search props
    allDataSources?: DataSource[]
    onAddExistingDataObject?: (selectedDataObjects: DataSource[]) => void
}

const HeaderSection: React.FC<HeaderSectionProps> = ({
    isInFolderView,
    selectedFolder,
    onBackToMain,
    onShowCreateFolder,
    onAddDataObject,
    onShowFeedback,
    disabled = false,
    isSelecting = false,
    selectedCount = 0,
    onToggleSelect,
    onBulkMove,
    onBulkDelete,
    onSelectAll,
    onClearSelection,
    hasSelectableItems = false,
    allDataSources = [],
    onAddExistingDataObject
}) => {
    const [showAddExistingModal, setShowAddExistingModal] = useState(false)
    const [selectedDataObjects, setSelectedDataObjects] = useState<DataSource[]>([])
    const [showInfoModal, setShowInfoModal] = useState(false)
    const { t } = useTranslate();

    const handleSelectDataObject = (dataObject: DataSource) => {
        setSelectedDataObjects(prev =>
            prev.includes(dataObject)
                ? prev.filter(obj => obj !== dataObject)
                : [...prev, dataObject]
        )
    }

    const handleAddExistingDataObjects = () => {
        if (onAddExistingDataObject && selectedDataObjects.length > 0) {
            onAddExistingDataObject(selectedDataObjects)
            setSelectedDataObjects([])
            setShowAddExistingModal(false)
        }
    }

    const folderDataSources = allDataSources.filter(ds =>
        selectedFolder ? ds.folderId === selectedFolder.id : !ds.folderId
    )

    const getIcon = (view: string) => {
        if (view === 'move') return <MdDriveFileMove className="mr-2 w-8 h-8 text-white" />
        if (view === 'delete') return <RiDeleteBin2Fill className="mr-2 w-8 h-8 text-white" />
        return <MdOutlineSelectAll className="mr-2 w-8 h-8 text-white" />
    }

    return (
        <div className="flex flex-col space-y-fluid-md mb-0 mt-fluid-lg">
            {/* Data Doctor Header and Button Section on same line */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-fluid-sm sm:space-y-0 sm:space-x-fluid-md">
                {/* Left side: Back button or Data Doctor Header */}
                {isInFolderView ? (
                    !isSelecting && (
                        <button
                            type="button"
                            onClick={onBackToMain}
                            className="w-auto inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group"
                            style={{
                                background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                                boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.15) translateY(-3px)';
                                e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(220, 38, 38, 0.6), 0 12px 15px -6px rgba(220, 38, 38, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = '';
                                e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)';
                            }}
                        >
                            <TbArrowBackUp className="mr-2 w-5 h-5 text-white" />
                            {t('Back to Main View')}
                        </button>
                    )
                ) : (
                    /* Data Doctor Header Section - Pure Branding */
                    !isSelecting && (
                        <div className="relative">
                            {/* Ambient background effect - subtle and non-interactive */}
                            <div className="absolute inset-0 bg-gradient-to-r from-red-50/20 to-pink-50/20 rounded-2xl blur-3xl opacity-30"></div>

                            <div className="relative flex flex-col space-y-1">
                                {/* Main brand title - clean and prominent */}
                                <div className="text-2xl font-black tracking-tight flex items-center gap-1.5">
                                    <span className="bg-gradient-to-r from-red-700 via-red-600 to-pink-600 bg-clip-text text-transparent">
                                        DATA DOCTOR
                                    </span>
                                    {/* Info button */}
                                    <button
                                        type="button"
                                        onClick={() => setShowInfoModal(true)}
                                        className="text-red-600 hover:text-red-800 transition-colors duration-200 hover:scale-110"
                                        title="About Data Doctor"
                                        aria-label="About Data Doctor"
                                    >
                                        <FaUserDoctor className="w-7 h-7" />
                                    </button>
                                    <ContextualHelpLink videoId="tv-001" />
                                </div>

                                {/* Tagline - sophisticated and understated */}
                                <div className="text-xs font-medium tracking-[0.15em] uppercase text-gray-600 leading-relaxed opacity-75">
                                    <span className="block text-[10px] leading-tight">
                                        {t('Where Data Wellness & Hygiene Meets Intelligence')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )
                )}

                {/* Selection Status when selecting */}
                {isSelecting && (
                    <div className="text-xl font-semibold text-black">
                        {selectedCount} {t('item(s) selected')}
                    </div>
                )}

                <div className="flex flex-row space-x-fluid-sm w-auto">
                    {isInFolderView ? (
                        <div className="flex flex-row space-x-fluid-sm w-auto">
                            {!isSelecting ? (
                                <div className="flex flex-row space-x-fluid-sm">
                                    {/* Data Admission Button */}
                                    <button
                                        type="button"
                                        onClick={onAddDataObject}
                                        className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{
                                            background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                                            boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)'
                                        }}
                                        disabled={disabled}
                                        onMouseEnter={(e) => {
                                            if (!disabled) {
                                                e.currentTarget.style.transform = 'scale(1.15) translateY(-3px)';
                                                e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(220, 38, 38, 0.6), 0 12px 15px -6px rgba(220, 38, 38, 0.4)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = '';
                                            e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)';
                                        }}
                                    >
                                        <HiOutlineFolderDownload className="mr-2 w-5 h-5 text-white" />
                                        {t('Data Admission')}
                                    </button>

                                    {/* Removed Manage Button - moved to MainView before Reports section */}
                                </div>
                            ) : (
                                /* Selection Mode - controls handled in FolderView */
                                <div></div>
                            )}
                        </div>
                    ) : (
                        /* Main View Controls - Create Folder and Data Admission buttons stacked vertically */
                        <div className="flex flex-col space-y-fluid-xs w-auto">
                            {!isSelecting ? (
                                <div className="flex flex-col space-y-fluid-xs">
                                    {/* Create Folder Button */}
                                    <button
                                        type="button"
                                        onClick={onShowCreateFolder}
                                        className="inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{
                                            background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                                            boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.15) translateY(-3px)';
                                            e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(220, 38, 38, 0.6), 0 12px 15px -6px rgba(220, 38, 38, 0.4)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = '';
                                            e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)';
                                        }}
                                    >
                                        <BsFolderPlus className="mr-2 w-7 h-7" />
                                        {t('Create Folder')}
                                    </button>

                                    {/* Data Admission Button */}
                                    <button
                                        type="button"
                                        onClick={onAddDataObject}
                                        className="inline-flex items-center justify-center px-6 py-3 text-white font-semibold rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{
                                            background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                                            boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)'
                                        }}
                                        disabled={disabled}
                                        onMouseEnter={(e) => {
                                            if (!disabled) {
                                                e.currentTarget.style.transform = 'scale(1.15) translateY(-3px)';
                                                e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(220, 38, 38, 0.6), 0 12px 15px -6px rgba(220, 38, 38, 0.4)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = '';
                                            e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)';
                                        }}
                                    >
                                        <HiOutlineFolderDownload className="mr-2 w-7 h-7" />
                                        {t('Data Admission')}
                                    </button>


                                </div>
                            ) : (
                                /* Selection Mode - controls handled in MainView */
                                <div></div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Info Modal */}
            {showInfoModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowInfoModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8 relative" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={() => setShowInfoModal(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                            title="Close"
                            aria-label="Close"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="text-center mb-6">
                            <h2 className="text-2xl font-black tracking-tight">
                                <span className="bg-gradient-to-r from-red-700 via-red-600 to-pink-600 bg-clip-text text-transparent">
                                    DATA DOCTOR
                                </span>
                            </h2>
                            <p className="text-xs text-gray-500 tracking-widest uppercase mt-1">{t('Where Data Wellness & Hygiene Meets Intelligence')}</p>
                        </div>

                        <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
                            <p>
                                <strong>Data Doctor</strong> {t('is your intelligent data quality management platform. Upload, analyze, and diagnose data health across your organization.')}
                            </p>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-red-50 rounded-xl p-3">
                                    <p className="font-semibold text-red-700 text-xs mb-1">{t('Upload & Organize')}</p>
                                    <p className="text-xs text-gray-600">{t('CSV, XLS, XLSX files organized into folders')}</p>
                                </div>
                                <div className="bg-red-50 rounded-xl p-3">
                                    <p className="font-semibold text-red-700 text-xs mb-1">{t('Automated Analysis')}</p>
                                    <p className="text-xs text-gray-600">{t('AI-powered data quality diagnostics')}</p>
                                </div>
                                <div className="bg-red-50 rounded-xl p-3">
                                    <p className="font-semibold text-red-700 text-xs mb-1">{t('Quality Reports')}</p>
                                    <p className="text-xs text-gray-600">{t('Detailed health scores across 6 dimensions')}</p>
                                </div>
                                <div className="bg-red-50 rounded-xl p-3">
                                    <p className="font-semibold text-red-700 text-xs mb-1">{t('Actionable Insights')}</p>
                                    <p className="text-xs text-gray-600">{t('Prescriptions to improve your data quality')}</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 text-center">
                            <button
                                type="button"
                                onClick={() => setShowInfoModal(false)}
                                className="inline-flex items-center justify-center px-6 py-2 text-white font-semibold rounded-full text-sm transition-all duration-200"
                                style={{
                                    background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                                    boxShadow: '0 2px 8px -2px rgba(220, 38, 38, 0.25)'
                                }}
                            >
                                {t('Got it')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default HeaderSection